import { Link } from "react-router-dom";
import { Reveal } from "@/components/Reveal";
import HeroSlideshow from "@/components/HeroSlideshow";
import Marquee from "@/components/Marquee";
import {
  Building2, FlaskConical, HeartPulse, Ruler, Layers, Sparkles,
  Paintbrush, HardHat, ArrowRight, Landmark, Hotel, Scale, BookOpen, GraduationCap,
} from "lucide-react";

const SERVICES = [
  { icon: Building2, title: "Office & Facility Moves", desc: "Zero-downtime commercial relocation planned around your operations — from a single suite to full campus transitions." },
  { icon: FlaskConical, title: "Biotech & Lab Services", desc: "Chain-of-custody lab relocation for sensitive instruments, samples, and regulated research environments." },
  { icon: HeartPulse, title: "Healthcare Relocation", desc: "Specialized transport for diagnostic equipment and medical assets in active care facilities." },
  { icon: Layers, title: "Commercial Flooring", desc: "Carpet, laminate, hardwood, and tile — expert commercial flooring installation for every industry." },
  { icon: Ruler, title: "Interior Design & Space Planning", desc: "Workplace strategy, space planning, and interior design that make every square foot work harder." },
  { icon: Sparkles, title: "Corporate Cleaning", desc: "Commercial cleaning services and short-term storage to keep transitions spotless and assets secure." },
  { icon: Paintbrush, title: "Painting & Refresh", desc: "Professional painting to deliver move-in-ready spaces on day one." },
  { icon: HardHat, title: "Small Commercial Construction", desc: "Punch-list construction, build-outs, and facility decommissioning handled by one accountable team." },
];

const INDUSTRIES = [
  { icon: Landmark, name: "Government" },
  { icon: HeartPulse, name: "Medical Facilities" },
  { icon: Hotel, name: "Hospitality" },
  { icon: Scale, name: "Law Firms" },
  { icon: BookOpen, name: "Libraries & Museums" },
  { icon: GraduationCap, name: "Universities & Schools" },
];

const BADGES = [
  { num: "01", label: "Ashburn, VA Headquarters" },
  { num: "02", label: "Commercial · Residential · Specialty" },
  { num: "03", label: "Local & Long Distance" },
  { num: "04", label: "Service-First Culture" },
];

export default function Home() {
  return (
    <main data-testid="home-page">
      <HeroSlideshow />
      <Marquee />

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-slate-200 border border-slate-200">
          {BADGES.map((b, i) => (
            <Reveal key={b.num} delay={i * 0.08} className="bg-white p-8">
              <p className="font-display text-brand font-bold text-sm mb-2">{b.num}</p>
              <p className="font-display font-semibold text-navy text-lg leading-snug">{b.label}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-24">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.3em] font-semibold text-brand mb-3">What We Do</p>
          <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
            <h2 className="font-display font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-navy max-w-2xl leading-tight">
              Commercial moving services that meet your needs
            </h2>
            <Link to="/workplace-solutions" data-testid="home-services-link"
              className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-brand hover:text-brand-hover transition-colors">
              All Workplace Services <ArrowRight size={15} />
            </Link>
          </div>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {SERVICES.map((s, i) => (
            <Reveal key={s.title} delay={(i % 4) * 0.08}
              className="group bg-white border border-slate-200 rounded-md p-7 hover:border-brand hover:shadow-lg hover:shadow-brand/5 hover:-translate-y-1 transition-all duration-300">
              <s.icon size={28} className="text-brand mb-5 group-hover:scale-110 transition-transform" />
              <h3 className="font-display font-semibold text-lg text-navy mb-2">{s.title}</h3>
              <p className="text-sm text-slate-600 leading-relaxed">{s.desc}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] font-semibold text-brand mb-3">Industries We Serve</p>
            <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
              <h2 className="font-display font-bold tracking-tight text-3xl sm:text-4xl text-navy">
                Trusted across every sector
              </h2>
              <Link to="/industries" data-testid="home-industries-link"
                className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-brand hover:text-brand-hover transition-colors">
                Explore Industries <ArrowRight size={15} />
              </Link>
            </div>
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {INDUSTRIES.map((s, i) => (
              <Reveal key={s.name} delay={i * 0.06}
                className="border border-slate-200 rounded-md p-6 text-center hover:border-brand hover:bg-brand-subtle transition-colors">
                <s.icon size={26} className="text-navy mx-auto mb-3" />
                <p className="font-display font-semibold text-sm text-navy">{s.name}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <Reveal className="relative">
            <div className="absolute -top-4 -left-4 w-24 h-24 border-t-4 border-l-4 border-brand" />
            <img
              src="/images/leadership.jpg"
              alt="Heather Harris, President and CEO of HLM Moving"
              data-testid="home-leadership-image"
              className="w-full aspect-[4/5] object-cover rounded-sm"
            />
          </Reveal>
          <Reveal delay={0.15}>
            <p className="text-xs uppercase tracking-[0.3em] font-semibold text-brand mb-3">Leadership</p>
            <h2 className="font-display font-bold tracking-tight text-3xl sm:text-4xl text-navy mb-2">Heather Harris</h2>
            <p className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-6">President &amp; Chief Executive Officer</p>
            <p className="text-slate-600 leading-relaxed mb-4">
              As President and CEO of HLM Moving, Heather Harris leads the company's strategic direction,
              operations, and client partnerships — building HLM Moving into a trusted partner for
              organizations across the region.
            </p>
            <p className="text-slate-600 leading-relaxed mb-8">
              A recognized expert in corporate facilities, relocation, and client relations, Heather has
              delivered innovative solutions for clients including Bloomberg, Amazon, Leidos, Volkswagen,
              Raytheon/RTX, and Capital One.
            </p>
            <Link to="/leadership" data-testid="home-leadership-link"
              className="inline-flex items-center gap-2 bg-navy hover:bg-navy-light text-white text-sm font-bold uppercase tracking-wide px-7 py-3.5 rounded-sm transition-colors">
              Meet Our Leadership <ArrowRight size={15} />
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="bg-navy relative overflow-hidden">
        <div className="absolute -right-24 -top-24 w-96 h-96 bg-brand/10 rounded-full blur-3xl" />
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 relative">
          <Reveal className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.3em] font-semibold text-brand mb-3">Ready When You Are</p>
            <h2 className="font-display font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-white leading-tight mb-6">
              Let's plan your next move
            </h2>
            <p className="text-white/60 leading-relaxed mb-9">
              From a single office to a full facility decommission, every move is handled with the
              planning, care, and communication your team deserves.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/contact" data-testid="home-cta-quote"
                className="inline-flex items-center gap-2 bg-brand hover:bg-brand-hover text-white text-sm font-bold uppercase tracking-wide px-8 py-4 rounded-sm transition-colors">
                Get a Quote <ArrowRight size={16} />
              </Link>
              <a href="tel:+15718509694" data-testid="home-cta-call"
                className="inline-flex items-center gap-2 border border-white/30 text-white hover:bg-white hover:text-navy text-sm font-bold uppercase tracking-wide px-8 py-4 rounded-sm transition-colors">
                Call (571) 850-9694
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
